import React, {useEffect, useRef, useState} from 'react'
import clsx from 'clsx'
import {useTranslation} from 'react-i18next'

import {validateVatId} from '../../../utils/vatApi'
import {VatCheckStatus, vatFieldState} from '../../../utils/vatFieldState'
import {
  countryCodeFromVatId,
  isEuVatCountry,
  looksLikeVatId,
  stripVatPrefix,
  vatExampleFor,
} from '../../../utils/vatIdFormat'

export type MemberType = 'personal' | 'business'

// The identity of a "checked" value is the (vat-id, country) PAIR, not the id alone. Changing EITHER
// must re-trigger validation: an edited id, OR a country change (e.g. the member fixes a country
// mismatch by switching the billing country to match the id). Keying on the pair is what makes the
// re-check fire on a country change and clears a stale mismatch/result.
const checkKey = (vatId: string, countryCode: string | null): string => `${vatId}@@${countryCode ?? ''}`

export interface BusinessVatFieldsProps {
  memberType: MemberType
  onMemberTypeChange: (memberType: MemberType) => void

  companyName: string
  onCompanyNameChange: (value: string) => void
  companyNameError?: string
  companyNameTouched?: boolean

  vatId: string
  onVatIdChange: (value: string) => void
  vatIdError?: string
  vatIdTouched?: boolean

  /** ISO-3166-1 alpha-2 code for the member's selected country, or null when none is chosen yet. */
  countryCode: string | null

  /** Fired on every status/in-flight change so the host form can gate "can I proceed". */
  onVatStatusChange: (status: VatCheckStatus, inFlight: boolean) => void

  /** Delay, in ms, between the UST-ID blur and the VIES call. Defaults to 400ms. */
  debounceMs?: number

  /**
   * Seeds the VIES state for a `vatId` that was already checked BEFORE this component mounted
   * (the account editor's use case: an existing member's previously-declared, already-verified
   * UST-ID). Defaults to 'idle' — the signup form's correct behavior, since its `vatId` is always
   * blank on mount. When given a non-'idle' value, the component shows that status immediately
   * (e.g. "✓ Validated: {{name}}") without requiring a blur or performing a VIES call, and reports
   * it upward via `onVatStatusChange` once on mount. Editing the field afterwards still resets to
   * 'idle' and re-checks on the next blur, same as any other value change.
   */
  initialStatus?: VatCheckStatus

  /** The company/registrant name to show alongside a non-idle `initialStatus`. */
  initialName?: string

  /**
   * Hides the personal/business radio selector, rendering only the company + UST-ID fields. The
   * account editor uses this: there the member-type decision is driven by an explicit "Convert to
   * business" / "Switch to personal" action outside this component, not an inline toggle. The
   * signup form leaves this false and keeps the selector.
   */
  hideMemberTypeSelector?: boolean
}

/**
 * Personal/business selector + (when business) company name + UST-ID with inline VIES
 * validation. Extracted out of Personinfo (signup, Task 3) so the account editor (Task 8) can
 * reuse the exact same selector, fields and five-state VIES feedback instead of re-implementing
 * the debounce/race-guard logic.
 *
 * The component owns the debounce and the VIES call; the host owns the field VALUES (so they sit
 * inside whatever form library the host already uses — Formik here) and is told about status
 * changes via `onVatStatusChange` so it can gate completion the same way `vatFieldState` renders
 * this component's own feedback (see src/utils/vatFieldState.ts).
 */
export function BusinessVatFields({
  memberType,
  onMemberTypeChange,
  companyName,
  onCompanyNameChange,
  companyNameError,
  companyNameTouched,
  vatId,
  onVatIdChange,
  vatIdError,
  vatIdTouched,
  countryCode,
  onVatStatusChange,
  debounceMs = 400,
  initialStatus = 'idle',
  initialName,
  hideMemberTypeSelector = false,
}: BusinessVatFieldsProps) {
  const {t} = useTranslation()
  const [status, setStatus] = useState<VatCheckStatus>(initialStatus)
  const [inFlight, setInFlight] = useState(false)
  const [validatedName, setValidatedName] = useState<string | undefined>(initialName)
  // True when the entered value can't be a USt-IdNr at all (wrong shape for the country) — caught
  // client-side before spending a VIES call, so the member gets the expected format immediately.
  const [formatInvalid, setFormatInvalid] = useState(false)
  // True when the UST-ID's own country prefix doesn't match the selected billing country (e.g. a DE
  // VAT ID with an Austrian address) — a business is registered in its VAT-ID's country, so these
  // must agree. Blocks completion with a clear message instead of validating the mismatched pair.
  const [countryMismatch, setCountryMismatch] = useState(false)

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestIdRef = useRef(0)
  // The vat_id value the current `status`/`validatedName` actually refers to. Any value that
  // doesn't match this is stale feedback — whether the member edited the field or the host reset
  // it externally (e.g. switching back to "Personal" clears vat_id on the parent). Seeded to the
  // MOUNT-TIME vat_id (a ref initializer only runs once) when the host vouches for it via a
  // non-idle `initialStatus`, so the vatId-watching effect below doesn't immediately treat the
  // pre-loaded value as "changed" and wipe the seeded status back to idle.
  const checkedValueRef = useRef<string | null>(initialStatus !== 'idle' ? checkKey(vatId, countryCode) : null)

  // EU country → it's an EU VAT-ID (USt-IdNr): show the country-code format hint and run VIES.
  // Non-EU country → the business has only a local Tax ID, not an EU VAT-ID: relabel the field,
  // drop the "country code + number" format expectation, and skip VIES/format/mismatch checks
  // entirely (the id is still recorded, and VAT is charged by country rate). `null` country (id
  // typed before the address on signup) stays on the EU/VAT path so VIES-by-prefix still works.
  const isEuVat = isEuVatCountry(countryCode)
  const isTaxIdCountry = !isEuVat

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    },
    []
  )

  useEffect(() => {
    if (initialStatus !== 'idle') onVatStatusChange(initialStatus, false)
    // Mount-only: reports the seeded status upward once so the host's "can I proceed" gate sees it
    // without waiting for a blur. Re-running on prop changes would re-report a stale initial value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const runCheck = (code: string, numberForVies: string, originalValue: string) => {
    const requestId = ++requestIdRef.current
    setInFlight(true)
    onVatStatusChange('idle', true)
    validateVatId(code, numberForVies).then((result) => {
      if (requestId !== requestIdRef.current) return // superseded by a newer edit/check
      checkedValueRef.current = checkKey(originalValue, countryCode)
      setInFlight(false)
      setStatus(result.status)
      setValidatedName(result.name)
      onVatStatusChange(result.status, false)
    })
  }

  // Re-evaluate whenever the value OR the selected country changes — a paste, a keystroke and a
  // country pick all land here, so the member never has to blur or re-edit to trigger the check.
  // Debounced so typing doesn't fire a VIES call per character; a paste resolves after one debounce.
  useEffect(() => {
    if (checkKey(vatId, countryCode) === checkedValueRef.current) return
    requestIdRef.current += 1 // invalidate any in-flight / pending-debounce check
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    setStatus('idle')
    setInFlight(false)
    setValidatedName(undefined)
    setFormatInvalid(false)
    setCountryMismatch(false)
    onVatStatusChange('idle', false)

    // Non-EU country: a local Tax ID, not an EU VAT-ID — no VIES, no format/mismatch checks.
    if (isTaxIdCountry) return

    const trimmedId = vatId.trim()
    // Country comes from the UST-ID's own prefix (DE123456789 → DE) when present, else the selected
    // billing country. This lets VIES run on signup where the UST-ID is entered BEFORE the address.
    const vatIdCountry = countryCodeFromVatId(trimmedId)
    const effectiveCountry = vatIdCountry ?? countryCode
    // The UST-ID's own country must match the selected billing country — flag the mismatch and block.
    if (vatIdCountry && countryCode && vatIdCountry !== countryCode) {
      checkedValueRef.current = checkKey(vatId, countryCode)
      setCountryMismatch(true)
      onVatStatusChange('invalid', false)
      return
    }
    // Auto-check only a value that could actually BE a VAT ID for that country, so neither a VIES call
    // nor an error fires on half-typed input. A finished-but-wrong value is flagged on blur instead.
    // VIES wants the number WITHOUT the country prefix (the member may paste one).
    if (!trimmedId || !effectiveCountry || !looksLikeVatId(trimmedId, effectiveCountry)) return
    debounceRef.current = setTimeout(
      () => runCheck(effectiveCountry, stripVatPrefix(trimmedId, effectiveCountry), vatId),
      debounceMs
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vatId, countryCode])

  // Flag a finished-but-invalid value when the member leaves the field (typing an incomplete number
  // never trips this; a plausible value is already auto-checked by the effect above). Also flags a
  // number with no country prefix when no billing country is selected — the format error then tells
  // the member to include the country code (e.g. DE123456789).
  const handleVatIdBlur = () => {
    // Non-EU Tax ID has no fixed country format — never flag it as format-invalid.
    if (isTaxIdCountry) return
    const trimmedId = vatId.trim()
    const effectiveCountry = countryCodeFromVatId(trimmedId) ?? countryCode
    if (!trimmedId || looksLikeVatId(trimmedId, effectiveCountry)) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    requestIdRef.current += 1
    checkedValueRef.current = checkKey(vatId, countryCode)
    setFormatInvalid(true)
    onVatStatusChange('invalid', false)
  }

  const viesState = vatFieldState(status, inFlight)

  return (
    <div className='business-vat-fields'>
      {/* Personal / business selector — 2 options, segmented via the project's existing radio style.
          Hidden in the account editor, where an explicit Convert/Switch action drives member type. */}
      {!hideMemberTypeSelector && (
        <div className='row mb-6'>
          <label className='col-lg-3 col-form-label fw-bold fs-6'>{t('VAT_MEMBER_TYPE_LABEL')}</label>
          <div
            className='col-lg-9 d-flex align-items-center'
            role='radiogroup'
            aria-label={t('VAT_MEMBER_TYPE_LABEL')}
          >
            <label className='form-check form-check-custom form-check-solid me-6'>
              <input
                className='form-check-input'
                type='radio'
                name='memberType'
                value='personal'
                checked={memberType === 'personal'}
                onChange={() => onMemberTypeChange('personal')}
              />
              <span className='form-check-label ms-2'>{t('VAT_MEMBER_TYPE_PERSONAL')}</span>
            </label>
            <label className='form-check form-check-custom form-check-solid'>
              <input
                className='form-check-input'
                type='radio'
                name='memberType'
                value='business'
                checked={memberType === 'business'}
                onChange={() => onMemberTypeChange('business')}
              />
              <span className='form-check-label ms-2'>{t('VAT_MEMBER_TYPE_BUSINESS')}</span>
            </label>
          </div>
        </div>
      )}

      {memberType === 'business' && (
        <>
          {/* Company name — required once "Business" is picked (a UST-ID means you're a business) */}
          <div className='row mb-6'>
            <label
              htmlFor='vat_company_name'
              className='col-lg-3 col-form-label required fw-bold fs-6'
            >
              {t('VAT_COMPANY_NAME_LABEL')}
            </label>
            <div className='col-lg-9 fv-row'>
              <input
                id='vat_company_name'
                type='text'
                placeholder={t('VAT_COMPANY_NAME_LABEL')}
                value={companyName}
                onChange={(e) => onCompanyNameChange(e.target.value)}
                className={clsx('form-control form-control-lg form-control-solid', {
                  'is-invalid': companyNameTouched && companyNameError,
                  'is-valid': companyNameTouched && !companyNameError,
                })}
              />
              {companyNameTouched && companyNameError && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{companyNameError}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* UST-ID with inline VIES validation — five visible states, see vatFieldState.ts */}
          <div className='row mb-6'>
            <label htmlFor='vat_id' className='col-lg-3 col-form-label required fw-bold fs-6'>
              {t(isTaxIdCountry ? 'VAT_TAX_ID_LABEL' : 'VAT_UST_ID_LABEL')}
            </label>
            <div className='col-lg-9 fv-row'>
              <input
                id='vat_id'
                type='text'
                placeholder={isTaxIdCountry ? t('VAT_TAX_ID_PLACEHOLDER') : vatExampleFor(countryCode)}
                value={vatId}
                onChange={(e) => onVatIdChange(e.target.value)}
                onBlur={handleVatIdBlur}
                className={clsx('form-control form-control-lg form-control-solid', {
                  'is-invalid':
                    (vatIdTouched && vatIdError) ||
                    formatInvalid ||
                    countryMismatch ||
                    viesState.variant === 'error',
                  'is-valid': vatIdTouched && !vatIdError && viesState.variant === 'success',
                })}
              />
              {/* Format guidance before the member types. EU: country-code + number example. Non-EU:
                  a general tax-id hint (no EU VAT format — conventions differ per country). */}
              <div className='form-text text-muted'>
                {isTaxIdCountry
                  ? t('VAT_TAX_ID_HINT')
                  : t('VAT_UST_ID_HINT', {example: vatExampleFor(countryCode)})}
              </div>
              {formatInvalid && (
                <div className='text-danger mt-2' role='alert'>
                  {t('VAT_UST_ID_FORMAT_ERROR', {example: vatExampleFor(countryCode)})}
                </div>
              )}
              {countryMismatch && (
                <div className='text-danger mt-2' role='alert'>
                  {t('VAT_UST_ID_COUNTRY_MISMATCH')}
                </div>
              )}
              {vatIdTouched && vatIdError && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{vatIdError}</span>
                  </div>
                </div>
              )}
              {viesState.variant === 'checking' && (
                <div className='text-muted mt-2' role='status'>
                  <span className='spinner-border spinner-border-sm me-2' />
                  {t('VAT_VIES_CHECKING')}
                </div>
              )}
              {viesState.variant === 'success' && (
                <div className='text-success mt-2'>
                  {/* Some member states (DE/ES/EL) confirm a VAT ID as valid but withhold the trader
                      name — the backend normalizes that to no name, so show a name-less confirmation
                      rather than "Validated: " with an empty/"---" name. */}
                  {validatedName
                    ? t('VAT_VIES_VALID', {name: validatedName})
                    : t('VAT_VIES_VALID_NO_NAME')}
                </div>
              )}
              {viesState.variant === 'error' && (
                <div className='text-danger mt-2' role='alert'>
                  {t('VAT_VIES_INVALID')}
                </div>
              )}
              {viesState.variant === 'warning' && (
                <div className='text-warning mt-2'>
                  <div>{t('VAT_VIES_UNAVAILABLE')}</div>
                  <div>{t('VAT_VIES_UNAVAILABLE_TAXABLE_NOTE')}</div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
